// Server-default models live in a static directory the operator drops files into; `npm run models`
// regenerates models/manifest.json (the lobby fetches it, since static hosts can't list a dir).
//
// This service is public, so the manifest is treated as untrusted input: every entry is reduced to
// a safe basename and fetched strictly same-origin under models/ — never an attacker-supplied path
// or absolute URL (no traversal, no SSRF/exfil). Content is validated again on import by magic
// bytes (see data/library.isModelBlob). The app has no upload endpoint; files are placed
// server-side by the operator.

const MODELS_DIR = 'models'
const MAX_ENTRIES = 200
const MAX_NAME = 120

export interface ServerModel {
  file: string // sanitized basename, e.g. "chair.glb"
  name: string // display label
  size?: number
  url: string // same-origin URL under models/
}

// Accept only a plain basename ending in .glb/.gltf — no path separators, no parent refs, no scheme.
const SAFE_FILE = /^[\w][\w .()-]{0,127}\.(glb|gltf)$/i

export function isSafeModelFile(file: unknown): file is string {
  return (
    typeof file === 'string' &&
    SAFE_FILE.test(file) &&
    !file.includes('..') &&
    !file.includes('/') &&
    !file.includes('\\')
  )
}

function entryOf(raw: unknown): { file?: unknown; name?: unknown; size?: unknown } {
  if (typeof raw === 'string') return { file: raw }
  if (raw && typeof raw === 'object') return raw as Record<string, unknown>
  return {}
}

// Pure: turn untrusted manifest data into a bounded list of same-origin ServerModels.
export function parseManifest(data: unknown, base: string): ServerModel[] {
  const list = Array.isArray(data)
    ? data
    : Array.isArray((data as { models?: unknown })?.models)
      ? (data as { models: unknown[] }).models
      : []

  const out: ServerModel[] = []
  for (const raw of list.slice(0, MAX_ENTRIES)) {
    const { file, name, size } = entryOf(raw)
    if (!isSafeModelFile(file)) continue
    out.push({
      file,
      name: (typeof name === 'string' ? name : file).slice(0, MAX_NAME),
      size: typeof size === 'number' && Number.isFinite(size) && size >= 0 ? size : undefined,
      url: `${base}${MODELS_DIR}/${encodeURIComponent(file)}`,
    })
  }
  return out
}

const isModelResponse = (res: Response) =>
  res.ok && !(res.headers.get('content-type') ?? '').includes('text/html')

export async function fetchServerModels(base: string): Promise<ServerModel[]> {
  if (typeof fetch !== 'function') return []
  let res: Response
  try {
    res = await fetch(`${base}${MODELS_DIR}/manifest.json`, { cache: 'no-cache' })
  } catch {
    return [] // offline / network error
  }
  // A missing manifest falls back to the SPA's index.html (200 text/html) — reject it.
  if (!isModelResponse(res)) return []
  try {
    return parseManifest(await res.json(), base)
  } catch {
    return []
  }
}

export async function fetchServerModel(model: ServerModel): Promise<File> {
  const res = await fetch(model.url)
  if (!isModelResponse(res)) throw new Error(`model unavailable (${res.status})`)
  return new File([await res.blob()], model.file, { type: 'model/gltf-binary' })
}
